import { Injectable } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

/**
 * Ever.Ag Design System product icon library (all of it), copied from
 * `Ever.Ag Design System/assets/icons` into `src/assets/svg-icons/everag`. They are
 * registered in the `ea` namespace (`<mat-icon svgIcon="ea:Samples-Outline">`); the
 * SVG paths are authored black and tinted with `currentColor` by a rule in
 * `src/styles/mdd.scss`. The list mirrors the folder; regenerate it when icons are added.
 */
export const EVERAG_ICONS = [
    'Add-Outline',
    'Alert-Duo',
    'Alert-Outline',
    'ArrowDown',
    'ArrowUp',
    'Barcode-Outline',
    'Camera-Duo',
    'Camera-Outline',
    'Cancelled',
    'CaretDoubleLeft',
    'CaretDoubleRight',
    'CaretDown',
    'CaretLeft',
    'CaretRight',
    'CaretUp',
    'CheckMark',
    'CloseX',
    'Comment-Duo',
    'Comment-Outline',
    'Complete-Duo',
    'Complete-Fill',
    'Complete-Outline',
    'Contract-Duo',
    'Contract-Outline',
    'Custom-Duo',
    'Custom-Outline',
    'Delete-Outline',
    'Download',
    'Dropoff',
    'Edit-Outline',
    'Factory-Duo',
    'Factory-Outline',
    'FarmTank-Duo',
    'FarmTank-Outline',
    'File',
    'FileCsv',
    'FileDoc',
    'FileJpg',
    'FilePdf',
    'FileXls',
    'Filter-Outline',
    'FinancialMarket-Duo',
    'FinancialMarket-Outline',
    'Folder',
    'FolderOpen',
    'Help-Duo',
    'Help-Fill',
    'Help-Outline',
    'History-Duo',
    'History-Outline',
    'Home-Duo',
    'Home-Outline',
    'Image-Duo',
    'Image-Outline',
    'InProgress-Duo',
    'InProgress-Fill',
    'InProgress-Outline',
    'Link',
    'LoadEmpty-Duo',
    'LoadEmpty-Fill',
    'LoadFull-Duo',
    'LoadFull-Fill',
    'LoadPartial-Duo',
    'LoadPartial-Fill',
    'Lock',
    'Lock-2',
    'Lock-3',
    'Map-Duo',
    'Map-Outline',
    'MapPin-Duo',
    'MapPin-Outline',
    'MapPinLine-Duo',
    'MapPinLine-Outline',
    'MenuMain',
    'MenuSub',
    'MenuSub2',
    'MilkCarton-Duo',
    'MilkCarton-Outline',
    'News-Duo',
    'News-Outline',
    'Notes-Duo',
    'Notes-Outline',
    'Office-Duo',
    'Office-Outline',
    'Offline-Duo',
    'Offline-Fill',
    'Offline-Outline',
    'Personnel-Duo',
    'Personnel-Outline',
    'Pickup',
    'Play-Outline',
    'Plus',
    'Print-Outline',
    'Production-Duo',
    'Production-Outline',
    'QrCode-Duo',
    'QrCode-Outline',
    'Receipt',
    'Refresh',
    'Receipt-2',
    'Remove-Outline',
    'Route-Duo',
    'Route-Outline',
    'Samples-Duo',
    'Samples-Outline',
    'Scan-Duo',
    'Scan-Outline',
    'Seal-Duo',
    'Seal-Outline',
    'Search-Duo',
    'Search-Outline',
    'Settings-Duo',
    'Settings-Outline',
    'Share-Outline',
    'Signature-Duo',
    'Signature-Outline',
    'Tanker-Duo',
    'Tanker-Outline',
    'Temperature-Duo',
    'Temperature-Outline',
    'Transport-Duo',
    'Transport-Outline',
    'Upload',
    'User-Duo',
    'User-Outline',
    'UserGroup-Duo',
    'UserGroup-Outline',
    'Video-Duo',
    'Video-Outline',
    'Warning',
    'Warning-2',
    'Warning-3',
    'Wash-Duo',
    'Wash-Outline',
    'Weight-Duo',
    'Weight-Outline'
];

@Injectable()
export class CustomIconService {
    constructor(
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer
    ) {}

    init() {
        EVERAG_ICONS.forEach((name) =>
            this.matIconRegistry.addSvgIconInNamespace(
                'ea',
                name,
                this.domSanitizer.bypassSecurityTrustResourceUrl(
                    `assets/svg-icons/everag/${name}.svg`
                )
            )
        );

        this.matIconRegistry.addSvgIcon(
            `dashboard`,
            this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/svg-icons/dashboard.svg')
        );

        this.matIconRegistry.addSvgIcon(
            `settings`,
            this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/svg-icons/settings.svg')
        );

        this.matIconRegistry.addSvgIcon(
            `disclaimer`,
            this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/svg-icons/disclaimer.svg')
        );

        this.matIconRegistry.addSvgIcon(
            `close`,
            this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/svg-icons/close.svg')
        );

        this.matIconRegistry.addSvgIcon(
            `check`,
            this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/svg-icons/check.svg')
        );

        this.matIconRegistry.addSvgIcon(
            `info-icon`,
            this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/svg-icons/info.svg')
        );

        this.matIconRegistry.addSvgIcon(
            `exit-to-app`,
            this.domSanitizer.bypassSecurityTrustResourceUrl('../assets/svg-icons/exit.svg')
        );

        this.matIconRegistry.addSvgIcon(
            'create',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/svg-icons/create.svg')
        );

        this.matIconRegistry.addSvgIcon(
            'producer-info',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/svg-icons/producer_info.svg')
        );

        this.matIconRegistry.addSvgIcon(
            'get_app',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/svg-icons/download.svg')
        );

        this.matIconRegistry.addSvgIcon(
            'drag_handle',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/svg-icons/drag_handle.svg')
        );

        this.matIconRegistry.addSvgIcon(
            'view_module',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/svg-icons/view_module.svg')
        );

        this.matIconRegistry.addSvgIcon(
            'filter',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/svg-icons/filter.svg')
        );

        this.matIconRegistry.addSvgIcon(
            'calculate',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/svg-icons/calculate.svg')
        );

        this.matIconRegistry.addSvgIcon(
            'calculator',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/svg-icons/calculator.svg')
        );

        this.matIconRegistry.addSvgIcon(
            'lock',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/svg-icons/lock.svg')
        );

        this.matIconRegistry.addSvgIcon(
            'mdd_expand_circle_right',
            this.domSanitizer.bypassSecurityTrustResourceUrl(
                'assets/svg-icons/expand_circle_right.svg'
            )
        );

        this.matIconRegistry.addSvgIcon(
            'mdd_feed',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/svg-icons/feed.svg')
        );

        this.matIconRegistry.addSvgIcon(
            'mdd_herd',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/svg-icons/herd.svg')
        );

        this.matIconRegistry.addSvgIcon(
            'mdd_milk',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/svg-icons/milk.svg')
        );
        this.matIconRegistry.addSvgIcon(
            'mdd_single_herd',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/svg-icons/single_herd.svg')
        );
        this.matIconRegistry.addSvgIcon(
            'mdd_atr',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/svg-icons/atr.svg')
        );

        this.matIconRegistry.addSvgIcon(
            'everag',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/svg-icons/everag-icon.svg')
        );
    }
}
